import React from "react";
import { reportClientError } from "@/lib/errorReporter";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export default class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Auto-recover from stale chunk/module errors with cache-busting reload
    if (
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk')
    ) {
      const reloadKey = 'chunk_reload_' + window.location.pathname;
      const lastReload = sessionStorage.getItem(reloadKey);
      if (!lastReload || Date.now() - Number(lastReload) > 15000) {
        sessionStorage.setItem(reloadKey, String(Date.now()));
        const url = new URL(window.location.href);
        url.searchParams.set('v', String(Date.now()));
        window.location.replace(url.toString());
        return;
      }
    }
    console.error("[AppErrorBoundary] Unhandled error:", error);
    console.error("[AppErrorBoundary] Component stack:", info.componentStack);
    reportClientError({
      type: 'react_error',
      message: error.message || String(error),
      stack: (error.stack || '') + '\n' + (info.componentStack || ''),
      severity: 'critical',
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Technical details (message/stack) are only shown in dev builds — production users
      // never see raw error/stack text, they get a generic message instead.
      const showDetails = import.meta.env.DEV;
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
          <div className="max-w-xl w-full rounded-xl border border-border bg-card p-6">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We hit an unexpected error. Please reload the page — if it keeps happening, try going back to the home page.
            </p>
            {showDetails && (
              <pre className="mt-4 max-h-60 overflow-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                {this.state.error?.message || 'No message'}
                {'\n\n'}
                {this.state.error?.stack || 'No stack'}
              </pre>
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                className="inline-flex h-10 items-center justify-center rounded-md border border-border px-4 text-sm font-medium"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
